import { ComponentProps, useEffect, useMemo, useState } from 'react'
import { PayPalScriptProvider, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { PayPalRuntimeContext, PayPalRuntime } from './PayPalRuntime'
import { get } from 'src/functions/fetch'

type Props = ComponentProps<typeof PayPalScriptProvider> & { pathname: string; managedShop?: number }

const ActivateSdk = ({ runtime }: { runtime: PayPalRuntime }) => {
  const [, dispatch] = usePayPalScriptReducer()
  useEffect(() => {
    if (runtime.status === 'ready') dispatch({ type: 'resetOptions', value: { ...runtime.options } })
  }, [runtime.status, runtime.options, dispatch])
  return null
}

/** Keep SDK context across navigation; load the payment SDK when a payment page needs it. */
export default function PayPalPageProvider({ pathname, children, managedShop, ...props }: Props) {
  const needsPaymentSdk = pathname === '/kasse'
    || pathname.startsWith('/bestellung/')
    || pathname === '/paypal-wallet-test'

  const [selected, setSelected] = useState<{ clientId: string; applePay: boolean } | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!managedShop) return
    setSelected(null)
    setFailed(false)
    if (!needsPaymentSdk) return
    const controller = new AbortController()
    let current = true
    const timeout = setTimeout(() => { controller.abort(); if (current) setFailed(true) }, 15000)
    void get({ url: `paypal/configuration?shop=${managedShop}`, signal: controller.signal }).then(value => {
      if (!current || controller.signal.aborted) return
      const row = value as { shop?: unknown; clientId?: unknown; applePay?: unknown } | null
      if (row?.shop !== managedShop || typeof row.clientId !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(row.clientId)
          || typeof row.applePay !== 'boolean') throw new Error('Invalid payment configuration')
      setSelected({ clientId: row.clientId, applePay: row.applePay })
    }).catch(() => { if (current) setFailed(true) }).finally(() => clearTimeout(timeout))
    return () => { current = false; clearTimeout(timeout); controller.abort() }
  }, [managedShop, needsPaymentSdk])

  const runtime = useMemo<PayPalRuntime>(() => {
    const components = String(props.options.components || 'buttons').split(',').filter(value => value !== 'applepay')
    if (selected?.applePay) components.push('applepay')
    return {
      status: failed ? 'error' : selected && needsPaymentSdk ? 'ready' : 'loading',
      clientId: selected?.clientId || '', applePay: selected?.applePay || false,
      options: { ...props.options, 'client-id': selected?.clientId || '', components: components.join(',') },
    }
  }, [selected, failed, needsPaymentSdk, props.options])

  return <PayPalScriptProvider {...props} deferLoading={managedShop ? true : !needsPaymentSdk}>
    {managedShop ? <PayPalRuntimeContext.Provider value={runtime}>
      <ActivateSdk runtime={runtime} />
      {children}
    </PayPalRuntimeContext.Provider> : children}
  </PayPalScriptProvider>
}
